import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { env } from '../../config/env';
import { VIDEO_CONSTANTS } from '../constants/videoConstants';
import { CHAT_IMAGE_PROCESSING_CONSTANTS } from '../constants/chatImageProcessingConstants';
import type { UserMediaEntity } from '../entities/userMedia/userMediaEntity';
import { MediaType } from '../enums/mediaType';
import { ChatUtils } from './chatUtils';
import { MathUtils } from './mathUtils';
import { NATIVELY_PLAYABLE_VIDEO_MIME_TYPES, SUPPORTED_COMMUNITY_IMAGE_MIME_TYPES, SUPPORTED_IMAGE_MIME_TYPES, SUPPORTED_VIDEO_MIME_TYPES } from './videoFormatUtils';
import type { UploadFileInfo } from '../entities/publish/uploadFileInfo';
import { ALLOWED_PORNOGRAPHY_LIKELIHOOD } from '../enums/pornographyLikelihood';
import type { MediaInfoEntity } from '../entities/info/mediaInfoEntity';

/**
 * Media Utilities
 * 
 * Static utility methods for media operations.
 * Pure functions with no state - can be used anywhere.
 * Works with domain entities (not DTOs).
 */
export class MediaUtils {
  /**
   * Compose a unique video player key based on screen and media IDs
   */
  static composeVideoPlayerKey(screenKey: string, mediaId: string): string {
    return `video_${screenKey}_${mediaId}`;
  }

  /**
   * Join a relative path onto the default Firebase Storage bucket prefix,
   * guaranteeing exactly one `o/` download-API segment between the bucket and
   * the object path — regardless of whether the prefix and/or the stored path
   * already carry it.
   *
   * A valid Storage download URL is `…/v0/b/<bucket>/o/<encoded-path>`. Two
   * env shapes exist in this codebase: Flutter's compiled runtime prefix ends
   * at `…/<bucket>/` (no `o/`), while the `.env` files currently end at
   * `…/<bucket>/o/`. Symmetrically, client-uploaded paths are persisted via
   * `downloadUrl.replaceFirst(prefix, '')`, so they keep a leading `o/` when
   * that prefix lacked it (e.g. `o/feedback%2F…?alt=media&token=…`) and drop it
   * otherwise. Blind concatenation therefore yields either a doubled `…/o/o/…`
   * (both carry it → `400 Invalid HTTP method/URL pair`) or a missing segment
   * (neither carries it). Normalising to one segment is correct for all four
   * prefix/path combinations and stays correct if the env value changes.
   */
  static joinDefaultBucketUrl(relativePath: string): string {
    const prefix = env.DEFAULT_BUCKET_PREFIX;
    const prefixHasSegment = prefix.endsWith('o/');
    const pathHasSegment = relativePath.startsWith('o/');

    if (prefixHasSegment && pathHasSegment) {
      // Both carry the `o/` segment — drop the duplicate from the path.
      return prefix + relativePath.slice(2);
    }
    if (!prefixHasSegment && !pathHasSegment) {
      // Neither carries it — insert exactly one.
      return `${prefix}o/${relativePath}`;
    }
    // Exactly one side carries it — already a single segment.
    return prefix + relativePath;
  }

  /**
   * Get thumbnail URL for media (matching Flutter logic)
   */
  static getMediaThumbnailUrl(media: UserMediaEntity): string | null {
    if (!media.relativeThumbnailPath) {
      return null;
    }

    // Local file path (for uploads in progress)
    if (media.isLocal) {
      return media.relativeThumbnailPath;
    }

    // Content media uses CDN
    if (media.isContent) {
      return `${env.CDN_NAME}${media.relativeThumbnailPath}`;
    }

    // Other media uses default bucket
    return MediaUtils.joinDefaultBucketUrl(media.relativeThumbnailPath);
  }

  /**
   * Build the CDN thumbnail URL for a notification's media info.
   * `MediaInfoEntity` sibling of `getMediaThumbnailUrl(UserMediaEntity)` — the
   * inbox notification thumbnails hold a `MediaInfoEntity` whose `thumbnail` is a
   * plain CDN-relative path (always content media), so this mirrors the
   * `isContent` branch of `getMediaThumbnailUrl`.
   */
  static getMediaInfoThumbnailUrl(mediaInfo: MediaInfoEntity): string {
    return `${env.CDN_NAME}${mediaInfo.thumbnail}`;
  }

  /**
   * Check if media is a video (type 1)
   */
  static isVideo(media: UserMediaEntity): boolean {
    return media.type === MediaType.VIDEO;
  }

  /**
   * Check if a notification's media info is a video (type 1).
   * `MediaInfoEntity` sibling of `isVideo(UserMediaEntity)` — used by the inbox
   * notification items, which hold a `MediaInfoEntity`, not a `UserMediaEntity`.
   */
  static mediaInfoIsVideo(mediaInfo?: MediaInfoEntity | null): boolean {
    return mediaInfo?.type === MediaType.VIDEO;
  }

  /**
   * Check if media is an image (type 0)
   */
  static isImage(media: UserMediaEntity): boolean {
    return media.type === MediaType.IMAGE;
  }

  /**
   * Get media URL for playback (matching Flutter logic)
   * Returns HLS manifest URL for web browsers
   *
   * Note: relativePath from backend should be in format: /media/{userId}/{videoId}/
   * We append the manifest filename to construct the full URL
   *
   * @param has4kAccess - When true, serves the full-quality (`master.m3u8`)
   *   manifest; otherwise the light (`master_light.m3u8`) manifest. Mirrors
   *   Flutter `UserMedia.path` which selects via `ContentUtils.has4kAccess`
   *   (`lib/data/models/user_media.dart:106-118`). Polarity: true => full quality.
   */
  static getMediaUrl(media: UserMediaEntity, has4kAccess: boolean): string | null {
    if (!media.relativePath) {
      console.warn('⚠️ [MediaUtils] No relativePath!');
      return null;
    }

    // Local file path
    if (media.isLocal) {
      return media.relativePath;
    }

    // For video content on web, use HLS (.m3u8) for better browser support
    // Match Flutter logic: Uri.decodeFull('$root$relativePath$manifestFileName')
    if (this.isVideo(media) && media.isContent) {
      const manifestFile = has4kAccess
        ? VIDEO_CONSTANTS.MASTER_HLS_NAME
        : VIDEO_CONSTANTS.MASTER_LIGHT_HLS_NAME;

      // Decode the relativePath to match Flutter's Uri.decodeFull()
      const decodedPath = decodeURIComponent(media.relativePath);

      // Append the manifest filename
      const fullPath = decodedPath.endsWith('/')
        ? `${decodedPath}${manifestFile}`
        : `${decodedPath}/${manifestFile}`;

      return `${env.CDN_NAME}${fullPath}`;
    }

    // Content media uses CDN
    if (media.isContent) {
      return `${env.CDN_NAME}${media.relativePath}`;
    }

    // Other media uses default bucket
    return MediaUtils.joinDefaultBucketUrl(media.relativePath);
  }

  /**
  * Build the video URL with CDN authentication token (fallback method)
  * Only used when UserMediaEntity is not available
  *
  * @param has4kAccess - When true, serves the full-quality (`master.m3u8`)
  *   manifest; otherwise the light (`master_light.m3u8`) manifest. Same polarity
  *   as `getMediaUrl` so the whole util speaks one vocabulary.
  */
  static buildVideoUrl(videoId: string, cdnToken: string, has4kAccess: boolean): string {
    const cdnBaseUrl = env.CDN_NAME;

    if (!cdnBaseUrl) {
      throw new Error('VITE_CDN_NAME environment variable not set');
    }

    // Use HLS for web (better browser support)
    const manifestFile = has4kAccess
      ? VIDEO_CONSTANTS.MASTER_HLS_NAME
      : VIDEO_CONSTANTS.MASTER_LIGHT_HLS_NAME;

    // Build URL: CDN_NAME/relativePath/manifest?hdnts={token}
    // Note: This is a fallback - prefer using MediaUtils with UserMediaEntity
    const videoUrl = `${cdnBaseUrl}media/${videoId}/${manifestFile}?hdnts=${cdnToken}`;

    return videoUrl;
  }

  /**
   * Get display name for media creator
   */
  static getCreatorName(media: UserMediaEntity): string {
    return media.userName || 'Unknown';
  }

  /**
   * Format view count (e.g., 1.2K, 1.5M)
   * Delegates to MathUtils for consistent number formatting
   */
  static formatViewCount(count: number): string {
    return MathUtils.formatCount(count);
  }

  /**
   * Format duration from milliseconds to MM:SS or HH:MM:SS
   * Uses Math.ceil to show "which second you're in" rather than "seconds completed"
   * This makes the display feel more responsive (e.g., 0.5s shows as 0:01, not 0:00)
   */
  static formatDuration(milliseconds: number): string {
    // Use ceil to round up - shows the current second you're in, not completed seconds
    // Special case: 0ms should show as 0, not 1
    const totalSeconds = milliseconds === 0 ? 0 : Math.ceil(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Format video progress display showing current time and total duration
   * @param currentTimeSeconds - Current playback time in seconds
   * @param durationSeconds - Total duration in seconds
   * @returns Formatted string like "0:15 / 2:30" or "1:05:30 / 2:15:45"
   */
  static formatVideoProgress(currentTimeSeconds: number, durationSeconds: number): string {
    const currentTimeMs = currentTimeSeconds * 1000;
    const durationMs = durationSeconds * 1000;
    return `${this.formatDuration(currentTimeMs)} / ${this.formatDuration(durationMs)}`;
  }

  /**
   * Check if video is premium based on duration
   * Premium videos are longer than 2 minutes (120 seconds)
   */
  static isPremiumVideo(duration?: number): boolean {
    if (!duration) return false;
    // Premium if longer than 2 minutes (120 seconds)
    const durationInSeconds = duration / 1000;
    return durationInSeconds > 120;
  }

  /**
   * Check if video is 4K based on resolution
   * 4K is 3840x2160 or higher on either dimension
   */
  static is4kVideo(resolution?: number): boolean {
    if (!resolution) return false;
    return resolution >= 2160;
  }

  /**
   * Check if content is NSFW based on the `explicit` field.
   * Matches Flutter's `explicit > PornographyLikelihood.allowedPornographyLikelihood.index`
   * (allowed = `PornographyLikelihood.possible`, index 3).
   */
  static isNsfwContent(explicit?: number | null): boolean {
    if (explicit === undefined || explicit === null) return false;
    return explicit > ALLOWED_PORNOGRAPHY_LIKELIHOOD;
  }

  /**
   * Check if video is landscape orientation
   * Based on aspect ratio greater than 1.0
   */
  static isLandscapeVideo(media: UserMediaEntity): boolean {
    return media.aspectRatio > 1.0;
  }

  /**
   * Check if video is portrait orientation
   * Based on aspect ratio less than 1.0
   */
  static isPortraitVideo(media: UserMediaEntity): boolean {
    return !this.isLandscapeVideo(media);
  }

  /**
   * Get transaction thumbnail URL from a relative path and CDN base URL
   * Matches Flutter's wallet transaction thumbnail logic
   *
   * @param relativePath - The relative path to the thumbnail
   * @param cdnName - The CDN base URL
   * @returns Full thumbnail URL
   */
  static getTransactionThumbnailUrl(relativePath: string, cdnName: string): string {
    return cdnName + relativePath;
  }

  static getUserContentUploadFilePath(uploadInfo: UploadFileInfo): string {
    return `media/${uploadInfo.uploadFilePath}/${uploadInfo.uploadFileName}`;
  }

  /**
   * Build the chat-media upload storage path.
   * Mirrors Flutter's chat upload path
   * (`app_storage_service.dart:188-189`):
   * `chat/${getStorageGroupChatId(currentFirebaseId, peerId)}/${uploadFileName}`.
   */
  static getChatContentUploadFilePath(uploadInfo: UploadFileInfo, storageGroupChatId: string): string {
    return `chat/${storageGroupChatId}/${uploadInfo.uploadFileName}`;
  }

  /**
   * Build the community-media upload storage path.
   *
   * Mirrors Flutter's community upload path
   * (`app_storage_service.dart:240-241`):
   * `community/${communityOwner}/${uploadFileName}`.
   *
   * `communityOwner` is the WALL OWNER — the creator whose community wall the
   * message is written to (`message.messageUsersInfo.owner`), NOT the uploader
   * (`medias_repository.dart:262` passes
   * `communityChatMessage.messageUsersInfo.owner`).
   *
   * `uploadInfo.uploadFileName` is `Date.now().toString()` with NO extension
   * (`video_process_service.dart:13` ⇔ `uploadFileInfoBuilder.ts:93`), so the
   * result is exactly 3 segments — the shape the transcode cloud function
   * asserts (`transcode_community_message_image.ts:32-39`; a 4th segment triggers
   * a permission-denied + raw-delete there).
   */
  static getCommunityContentUploadFilePath(
    uploadInfo: UploadFileInfo,
    communityOwner: string,
  ): string {
    return `community/${communityOwner}/${uploadInfo.uploadFileName}`;
  }

  /**
   * Derive the deterministic ciphertext upload paths for an encrypted chat
   * image + its thumbnail from the base 4-segment chat upload path.
   *
   * MUST agree byte-for-byte with the CF-side derivation in Task 5's reworked
   * `transcodeChatImage`
   * (`cloud_functions/.../transcoding/transcode_chat_image.ts`): given
   * `basePath = chat/{user1}/{user2}/{fileName}` (the value `chatService`
   * passes as the transcode payload's `filePath`), the CF derives
   * `fileDir = chat/{user1}/{user2}/` and writes the encrypted full image to
   * `{fileDir}image/{fileName}` (→ `serverPath`) and the encrypted thumbnail to
   * `{fileDir}thumbnail/{fileName}` (→ `thumbnail`). The client (Task 10
   * send flow) uploads each ciphertext to exactly these RELATIVE bucket object
   * paths so the CF's deterministic field-write lands on real objects — no
   * payload key carries the paths.
   *
   * @param basePath - The 4-segment `chat/{user1}/{user2}/{fileName}` path
   *   produced by `getChatContentUploadFilePath`.
   * @returns `{ imagePath, thumbnailPath }` — the two ciphertext destinations.
   */
  static getChatImageCiphertextUploadPaths(
    basePath: string,
  ): { imagePath: string; thumbnailPath: string } {
    const segments = basePath.split('/');
    const fileName = segments.pop() ?? '';
    const fileDir = segments.length > 0 ? `${segments.join('/')}/` : '';
    return {
      imagePath: `${fileDir}${CHAT_IMAGE_PROCESSING_CONSTANTS.IMAGE_SUBFOLDER}${fileName}`,
      thumbnailPath: `${fileDir}${CHAT_IMAGE_PROCESSING_CONSTANTS.THUMBNAIL_SUBFOLDER}${fileName}`,
    };
  }

  /**
   * Build the chat-media Media CDN video URL for encrypted HLS playback.
   *
   * Chat counterpart of {@link buildVideoUrl}. Where user-gen content lives at
   * `${CDN_NAME}media/{videoId}/master.m3u8`, transcoded chat video lives at the
   * deterministic relative path `chat/{user1}/{user2}/{documentId}/master.m3u8`
   * — the exact value the CF writes to the message's `serverPath` after
   * `transcodeChatVideo`. The `{user1}/{user2}` segment is the sorted user pair
   * produced by {@link ChatUtils.getStorageGroupChatId} (lexicographic `<`
   * ordering), so it agrees byte-for-byte with the upload path built in
   * `ChatService.uploadChatMedia` (Task 2, `chatService.ts:203-207`) and the
   * `generateChatMediaCdnTokens` CF's `PathGlobs` authorization scope (Task 4).
   *
   * Chat video is always served at full quality (`master.m3u8`); there is no
   * light/4k variant for chat media, so no `has4kAccess` parameter.
   *
   * @param user1 - One chat participant firebase id (e.g. current user).
   * @param user2 - The other chat participant firebase id (peer).
   * @param documentId - The chat message document id (the per-message folder).
   * @param hdnts - The Media CDN HMAC token from `generateChatMediaCdnTokens`.
   * @returns `${CDN_NAME}chat/{sortedUser1}/{sortedUser2}/{documentId}/master.m3u8?hdnts=<token>`
   */
  static getChatVideoUrl(
    user1: string,
    user2: string,
    documentId: string,
    hdnts: string,
  ): string {
    const cdnBaseUrl = env.CDN_NAME;

    if (!cdnBaseUrl) {
      throw new Error('VITE_CDN_NAME environment variable not set');
    }

    const sortedPair = ChatUtils.getStorageGroupChatId(user1, user2);
    const relativePath = `chat/${sortedPair}/${documentId}/${VIDEO_CONSTANTS.MASTER_HLS_NAME}`;

    return `${cdnBaseUrl}${relativePath}?hdnts=${hdnts}`;
  }

  /**
   * Build the community-media Media CDN video URL for encrypted HLS playback.
   *
   * Community counterpart of {@link getChatVideoUrl}. Transcoded community video
   * lives at the deterministic relative path
   * `community/{owner}/{messageId}/master.m3u8` — the exact root the transcode
   * cloud function writes (Task 2 `communityVideoOutputBasePath`,
   * `community_video_transcoding.ts:69-74`). Unlike chat there is NO user-pair to
   * sort: `owner` is the single community-wall owner
   * (`message.messageUsersInfo.owner`), so the path agrees byte-for-byte with the
   * server output root and the `generateCommunityMediaCdnTokens` CF's
   * `/community/{owner}/{messageId}/*` `PathGlobs` authorization scope (Task 7).
   *
   * Community video is always served at full quality (`master.m3u8`); there is no
   * light/4k variant, so no `has4kAccess` parameter.
   *
   * @param owner - The community-wall owner firebase id (the per-owner folder).
   * @param messageId - The community message document id (the per-message folder).
   * @param hdnts - The Media CDN HMAC token from `generateCommunityMediaCdnTokens`.
   * @returns `${CDN_NAME}community/{owner}/{messageId}/master.m3u8?hdnts=<token>`
   */
  static getCommunityVideoUrl(
    owner: string,
    messageId: string,
    hdnts: string,
  ): string {
    const cdnBaseUrl = env.CDN_NAME;

    if (!cdnBaseUrl) {
      throw new Error('VITE_CDN_NAME environment variable not set');
    }

    const relativePath = `community/${owner}/${messageId}/${VIDEO_CONSTANTS.MASTER_HLS_NAME}`;

    return `${cdnBaseUrl}${relativePath}?hdnts=${hdnts}`;
  }

  /**
   * Derive the deterministic PLAINTEXT community video-thumbnail upload path from
   * the 3-segment community upload base path.
   *
   * MUST agree byte-for-byte with the CF-side derivation in the reworked
   * `transcodeCommunityMessageVideo`
   * (`cloud_functions/.../transcoding/transcode_community_message_video.ts:104-117`):
   * given `basePath = community/{owner}/{fileName}` (the value the send flow
   * passes as the transcode payload's `filePath`), the CF derives
   * `fileDir = community/{owner}/` and reads the client-uploaded thumbnail at
   * `{fileDir}thumbnail/{fileName}`, then writes a tokenized default-bucket URL to
   * the message's `thumbnail` / `mediaInfo.thumbnail`. The client (Task 11 send
   * flow) uploads the plaintext JPEG to exactly this RELATIVE bucket object path
   * so the CF's deterministic field-write lands on a real object — no payload key
   * carries the path.
   *
   * The `thumbnail/` folder produced here is also the one the account-delete
   * prefix cleanup already covers (`app_storage.ts:91-98`).
   *
   * Community media is NOT E2EE, so this thumbnail is a plaintext JPEG (contrast
   * with the chat sibling `getChatImageCiphertextUploadPaths`, whose destinations
   * hold ciphertext).
   *
   * @param basePath - The 3-segment `community/{owner}/{fileName}` path produced
   *   by `getCommunityContentUploadFilePath`.
   * @returns `community/{owner}/thumbnail/{fileName}` — the plaintext destination.
   */
  static getCommunityVideoThumbnailUploadPath(basePath: string): string {
    const segments = basePath.split('/');
    const fileName = segments.pop() ?? '';
    const fileDir = segments.length > 0 ? `${segments.join('/')}/` : '';
    return `${fileDir}${CHAT_IMAGE_PROCESSING_CONSTANTS.THUMBNAIL_SUBFOLDER}${fileName}`;
  }

  // --- Content validation (ported from Flutter content_utils.dart) ---

  /**
   * Check if content duration is within allowed range
   * Matches Flutter ContentUtils.contentHasValidDuration
   */
  static contentHasValidDuration(durationSeconds: number): boolean {
    return durationSeconds >= remoteConfigService.contentMinSeconds &&
      durationSeconds <= remoteConfigService.contentMaxSeconds;
  }

  /**
   * Check if content is free (within free duration limit)
   * Allows one second buffer, matching Flutter logic
   */
  static isFreeContent(durationSeconds: number): boolean {
    return durationSeconds - 1 <= remoteConfigService.freeContentMaxSeconds;
  }

  /**
   * Check if content is premium (exceeds free duration limit)
   */
  static isPremiumContent(durationSeconds: number): boolean {
    return !this.isFreeContent(durationSeconds);
  }

  /**
   * Calculate premium content coin cost based on duration
   * Matches Flutter ContentUtils.getPremiumContentCoins exactly (including 1-second buffer)
   */
  static getPremiumContentCoins(durationSeconds: number): number {
    if (this.isFreeContent(durationSeconds)) return 0;
    return Math.ceil(
      (durationSeconds - 1 - remoteConfigService.freeContentMaxSeconds) / 60
    ) * remoteConfigService.premiumContentCoinsPerMinute;
  }

  /**
   * Free-content cap expressed in minutes (rounded up).
   * Matches Flutter ContentUtils.getFreeContentMaxMinutesFromConfigSeconds.
   */
  static getFreeContentMaxMinutesFromConfigSeconds(): number {
    return Math.ceil(remoteConfigService.freeContentMaxSeconds / 60);
  }

  /**
   * Format duration from seconds to MM:SS or HH:MM:SS (for local file metadata)
   */
  static formatDurationFromSeconds(seconds: number): string {
    return this.formatDuration(seconds * 1000);
  }

  // --- Video file utilities (browser-specific) ---

  /**
   * Check if a File is a video based on MIME type
   */
  static isVideoFile(file: File): boolean {
    return file.type.startsWith('video/');
  }

  /**
   * Check if a video file's MIME type is in the app's supported upload list.
   */
  static isSupportedVideoFormat(file: File): boolean {
    return (SUPPORTED_VIDEO_MIME_TYPES as readonly string[]).includes(file.type);
  }

  /**
   * Check if a video file can be decoded by native HTML5 <video> in-browser.
   * AVI and Matroska are accepted for upload but not playable client-side.
   */
  static isNativelyPlayableVideo(file: File): boolean {
    return NATIVELY_PLAYABLE_VIDEO_MIME_TYPES.includes(file.type);
  }

  /**
   * Extract video duration from a File object
   * @returns Duration in seconds
   */
  static getVideoFileDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(Math.ceil(video.duration));
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };

      video.src = URL.createObjectURL(file);
    });
  }

  /**
   * Extract video resolution from a File object
   */
  static getVideoFileResolution(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };

      video.src = URL.createObjectURL(file);
    });
  }

  // --- Image file utilities (browser-specific) ---
  //
  // The chat content flow accepts images as well as videos (Flutter fires
  // `transcodeChatImage` for `media.type == 0` — see
  // `cloud_functions_service.dart:168-169`). No Flutter equivalent: these
  // are browser-only File helpers mirroring the video ones above.

  /**
   * Check if a File is an image based on MIME type
   */
  static isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * Check if an image file's MIME type is in the app's supported upload list.
   */
  static isSupportedImageFormat(file: File): boolean {
    return (SUPPORTED_IMAGE_MIME_TYPES as readonly string[]).includes(file.type);
  }

  /**
   * Check if an image file's MIME type is decodable by the FROZEN community
   * transcode CF (raw upload, stock ffmpeg). Narrower than isSupportedImageFormat:
   * excludes HEIC/HEIF because ffmpeg-5/6 cannot decode them and the community flow
   * uploads the source file as-is (no client-side re-encode) — see
   * SUPPORTED_COMMUNITY_IMAGE_MIME_TYPES.
   */
  static isSupportedCommunityImageFormat(file: File): boolean {
    return (SUPPORTED_COMMUNITY_IMAGE_MIME_TYPES as readonly string[]).includes(file.type);
  }

  /**
   * Extract image resolution from a File object.
   * Same shape as getVideoFileResolution: resolves naturalWidth/naturalHeight
   * and revokes the object URL.
   */
  static getImageFileResolution(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(image.src);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };

      image.onerror = () => {
        URL.revokeObjectURL(image.src);
        reject(new Error('Failed to load image metadata'));
      };

      image.src = URL.createObjectURL(file);
    });
  }

  /**
   * Generate a thumbnail data URL from a video File
   * @param file - Video file
   * @param timeSeconds - Time in seconds to capture thumbnail (default: 1)
   * @returns Data URL (JPEG)
   */
  static generateVideoFileThumbnail(file: File, timeSeconds: number = 1): Promise<string> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      video.onloadedmetadata = () => {
        video.currentTime = Math.min(timeSeconds, video.duration);
      };

      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
        URL.revokeObjectURL(video.src);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to generate video thumbnail'));
      };

      video.src = URL.createObjectURL(file);
    });
  }

}
